/**
 * Provider scope: hard-block above-scope interventions at insertion.
 *
 * RI Statewide EMS Protocols 2026.01 §1.01 defines four provider
 * tiers (EMT → AEMT → AEMT-Cardiac → Paramedic). Each tier may
 * perform everything below it plus its own scope additions.
 *
 * The protocol-tool repo already has equivalent tables in
 * apps/protocol-tool/src/narrative/schema.ts. Web-local copy lives
 * here so the ePCR runtime can scope-gate without crossing the
 * package boundary. Keep these in sync; if we end up with a third
 * consumer, lift to packages/scope.
 *
 * Sprint K — hard scope blocks at insertion. Audit listed this as
 * 'warn-only today; should block.' Fixed below.
 */

import type { CertLevel } from '@/types';

export type ProviderLevel = 'EMT' | 'AEMT' | 'AEMT-Cardiac' | 'Paramedic';

const RANK: Record<ProviderLevel, number> = {
  'EMT':           1,
  'AEMT':          2,
  'AEMT-Cardiac':  3,
  'Paramedic':     4,
};

/**
 * Normalize the user's cert_level field (possibly 'EMT-B', 'RN', or
 * the legacy 'AEMT' that doesn't distinguish Cardiac) onto the four
 * canonical tiers. RN maps to Paramedic (RNs operating in pre-hospital
 * EMS work under MD direction equivalent to paramedic scope).
 */
export function toProviderLevel(cert: CertLevel | string | null | undefined): ProviderLevel {
  if (!cert) return 'EMT';
  const v = cert.replace(/\s+/g, '').toUpperCase();
  if (v === 'EMT' || v === 'EMT-B' || v === 'EMTB') return 'EMT';
  if (v === 'AEMT-CARDIAC' || v === 'AEMTC' || v === 'AEMTCARDIAC') return 'AEMT-Cardiac';
  if (v === 'AEMT')       return 'AEMT';
  if (v === 'PARAMEDIC')  return 'Paramedic';
  if (v === 'RN')         return 'Paramedic';
  return 'EMT';
}

export function scopeRank(level: ProviderLevel): number { return RANK[level]; }

export function withinScope(userLevel: ProviderLevel, required: ProviderLevel): boolean {
  return RANK[userLevel] >= RANK[required];
}

/* ============================================================
 * MIN_SCOPE_BY_DRUG — copied from protocol-tool/src/narrative/schema.ts
 * (verified parity 2026-05-03). When that file changes, update here.
 * ============================================================ */
export const MIN_SCOPE_BY_DRUG: Record<string, ProviderLevel> = {
  // EMT scope
  ASPIRIN:               'EMT',
  ASA:                   'EMT',
  OXYGEN:                'EMT',
  GLUCOSE:               'EMT',
  'ORAL GLUCOSE':        'EMT',
  ALBUTEROL:             'EMT',
  EPINEPHRINE:           'EMT',     // auto-injector at EMT level
  NALOXONE:              'EMT',
  NARCAN:                'EMT',
  // AEMT scope
  'NORMAL SALINE':       'AEMT',
  NS:                    'AEMT',
  'LACTATED RINGERS':    'AEMT',
  "LACTATED RINGER'S":   'AEMT',
  LR:                    'AEMT',
  D5W:                   'AEMT',
  D10:                   'AEMT',
  D50:                   'AEMT',
  DEXTROSE:              'AEMT',
  ZOFRAN:                'AEMT',
  ONDANSETRON:           'AEMT',
  GLUCAGON:              'AEMT',
  IPRATROPIUM:           'AEMT',
  // AEMT-Cardiac scope
  NITROGLYCERIN:         'AEMT-Cardiac',
  NTG:                   'AEMT-Cardiac',
  // Paramedic scope
  FENTANYL:              'Paramedic',
  KETAMINE:              'Paramedic',
  MIDAZOLAM:             'Paramedic',
  VERSED:                'Paramedic',
  MORPHINE:              'Paramedic',
  LORAZEPAM:             'Paramedic',
  ATIVAN:                'Paramedic',
  DIAZEPAM:              'Paramedic',
  AMIODARONE:            'Paramedic',
  ATROPINE:              'Paramedic',
  ROCURONIUM:            'Paramedic',
  ETOMIDATE:             'Paramedic',
  METHYLPREDNISOLONE:    'Paramedic',
  'SOLU-MEDROL':         'Paramedic',
  DEXAMETHASONE:         'Paramedic',
  ADENOSINE:             'Paramedic',
  DILTIAZEM:             'Paramedic',
  HALOPERIDOL:           'Paramedic',
  KETOROLAC:             'Paramedic',
  TORADOL:               'Paramedic',
  TRANEXAMIC:            'Paramedic',
  TXA:                   'Paramedic',
  HYDROCORTISONE:        'Paramedic',
  PREDNISONE:            'Paramedic',
  MAGNESIUM:             'Paramedic',
  'CALCIUM CHLORIDE':    'Paramedic',
  'CALCIUM GLUCONATE':   'Paramedic',
  'SODIUM BICARBONATE':  'Paramedic',
  PHENYLEPHRINE:         'Paramedic',
  NOREPINEPHRINE:        'Paramedic',
  TENECTEPLASE:          'Paramedic',
  TNK:                   'Paramedic',
};

/* ============================================================
 * MIN_SCOPE_BY_PROCEDURE
 * ============================================================ */
export const MIN_SCOPE_BY_PROCEDURE: Record<string, ProviderLevel> = {
  // EMT scope
  'BLOOD GLUCOSE':           'EMT',
  'PULSE OXIMETRY':          'EMT',
  BVM:                       'EMT',
  CPR:                       'EMT',
  AED:                       'EMT',
  // AEMT scope
  'PERIPHERAL IV':           'AEMT',
  'INTRAVENOUS ACCESS':      'AEMT',
  IV:                        'AEMT',
  'IV ACCESS':               'AEMT',
  // AEMT-Cardiac scope
  '12-LEAD ECG':             'AEMT-Cardiac',
  '12 LEAD ECG':             'AEMT-Cardiac',
  '12-LEAD':                 'AEMT-Cardiac',
  'TWELVE-LEAD ECG':         'AEMT-Cardiac',
  ECG:                       'AEMT-Cardiac',
  EKG:                       'AEMT-Cardiac',
  // Paramedic scope
  'ENDOTRACHEAL INTUBATION': 'Paramedic',
  ETI:                       'Paramedic',
  OETI:                      'Paramedic',
  NASOTRACHEAL:              'Paramedic',
  CRICOTHYROTOMY:            'Paramedic',
  DEFIBRILLATION:            'Paramedic',     // manual; AED is EMT
  'MANUAL DEFIBRILLATION':   'Paramedic',
  CARDIOVERSION:             'Paramedic',
  PACING:                    'Paramedic',
  'NEEDLE THORACOSTOMY':     'Paramedic',
  'IO ACCESS':               'Paramedic',
  IO:                        'Paramedic',
  'INTRAOSSEOUS':            'Paramedic',
};

export function minScopeForDrug(drug: string): ProviderLevel {
  const key = drug.replace(/\s+/g, ' ').trim().toUpperCase();
  return MIN_SCOPE_BY_DRUG[key] ?? 'AEMT';
}

export function minScopeForProcedure(name: string): ProviderLevel {
  const key = name.replace(/\s+/g, ' ').trim().toUpperCase();
  if (MIN_SCOPE_BY_PROCEDURE[key]) return MIN_SCOPE_BY_PROCEDURE[key];
  // Fuzzy fallback for free-text entries.
  if (/12[-\s]?lead|\bECG\b|\bEKG\b/i.test(name)) return 'AEMT-Cardiac';
  if (/intubation|cricothyrot|defibrillat|cardiovers|pacing|thoracostomy|RSI|IO\b|intraosseous/i.test(name)) return 'Paramedic';
  if (/IV\b|intravenous/i.test(name)) return 'AEMT';
  if (/BVM|AED|oxim|glucose|pulse\s*ox/i.test(name)) return 'EMT';
  return 'AEMT';
}

/**
 * Scope-gate check the UI calls before allowing a med / procedure
 * insertion. Returns null when allowed; returns a clinical-language
 * reason string when blocked.
 */
export function scopeBlockReason(
  userCert: CertLevel | string | null | undefined,
  required: ProviderLevel,
): string | null {
  const userLevel = toProviderLevel(userCert);
  if (withinScope(userLevel, required)) return null;
  return (
    `Above your ${userLevel} scope. This intervention requires ${required} or higher per RI ` +
    `Statewide EMS Protocols §1.01. Contact medical control to escalate, or document a medical-` +
    `direction order before proceeding.`
  );
}
